import React, { useId } from 'react';

export interface EmergencyStopProps {
  isTriggered?: boolean;
  label?: string;
  subLabel?: string;
  interactive?: boolean;
  width?: string | number;
  height?: string | number;
  className?: string;
  style?: React.CSSProperties;
  onClick?: (e: React.MouseEvent<SVGSVGElement>) => void;
  onPointerDown?: (e: React.PointerEvent<SVGSVGElement>) => void;
}

export const EmergencyStopSvg: React.FC<EmergencyStopProps> = ({
  isTriggered = false,
  label = 'EMERGENCY STOP',
  subLabel,
  interactive = true,
  width = '100%',
  height = '100%',
  className = '',
  style,
  onClick,
  onPointerDown,
}) => {
  const uid = useId().replace(/:/g, '');

  // Extract top and bottom text for circular legend plate
  let topText = 'EMERGENCY';
  let bottomText = 'STOP';

  if (subLabel) {
    topText = label;
    bottomText = subLabel;
  } else if (label) {
    const parts = label.trim().split(/\s+/);
    if (parts.length >= 2) {
      topText = parts[0]!;
      bottomText = parts.slice(1).join(' ');
    } else {
      topText = label;
      bottomText = 'STOP';
    }
  }

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 120 120"
      width={width}
      height={height}
      className={`interactive-emergency-stop ${className}`}
      style={{
        display: 'block',
        overflow: 'visible',
        cursor: interactive ? 'pointer' : 'default',
        userSelect: 'none',
        ...style,
      }}
      onClick={onClick}
      onPointerDown={onPointerDown}
    >
      <defs>
        {/* Yellow Safety Legend Plate Gradient (IEC 60947-5-5) */}
        <radialGradient id={`yellowPlateGrad-${uid}`} cx="45%" cy="40%" r="55%">
          <stop offset="0%" stopColor="#fef08a" />
          <stop offset="45%" stopColor="#facc15" />
          <stop offset="85%" stopColor="#eab308" />
          <stop offset="100%" stopColor="#ca8a04" />
        </radialGradient>

        {/* Outer Heavy-Duty Bezel Gradient */}
        <linearGradient id={`bezelGrad-${uid}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#475569" />
          <stop offset="35%" stopColor="#1e293b" />
          <stop offset="70%" stopColor="#0f172a" />
          <stop offset="100%" stopColor="#020617" />
        </linearGradient>

        {/* Chrome Actuator Shaft Sleeve Gradient (Visible when unlatched) */}
        <linearGradient id={`chromeShaftGrad-${uid}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#475569" />
          <stop offset="25%" stopColor="#cbd5e1" />
          <stop offset="45%" stopColor="#ffffff" />
          <stop offset="75%" stopColor="#94a3b8" />
          <stop offset="100%" stopColor="#334155" />
        </linearGradient>

        {/* Red Mushroom Dome Spherical Gradient (3D Safety Red) */}
        <radialGradient id={`mushroomDomeGrad-${uid}`} cx="40%" cy="35%" r="62%">
          <stop offset="0%" stopColor="#fca5a5" />
          <stop offset="18%" stopColor="#f87171" />
          <stop offset="42%" stopColor="#ef4444" />
          <stop offset="75%" stopColor="#dc2626" />
          <stop offset="92%" stopColor="#b91c1c" />
          <stop offset="100%" stopColor="#7f1d1d" />
        </radialGradient>

        {/* Pressed Latched Mushroom Dome Gradient (Deeper Shadow) */}
        <radialGradient id={`mushroomPressedGrad-${uid}`} cx="42%" cy="38%" r="62%">
          <stop offset="0%" stopColor="#f87171" />
          <stop offset="20%" stopColor="#ef4444" />
          <stop offset="50%" stopColor="#dc2626" />
          <stop offset="80%" stopColor="#991b1b" />
          <stop offset="100%" stopColor="#581010" />
        </radialGradient>

        {/* Center Concave Tactile Indent Gradient */}
        <radialGradient id={`centerIndentGrad-${uid}`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#000000" stopOpacity="0.35" />
          <stop offset="60%" stopColor="#000000" stopOpacity="0.12" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0.2" />
        </radialGradient>

        {/* Base Drop Shadow */}
        <filter id={`baseShadow-${uid}`} x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="4" stdDeviation="4.5" floodColor="#000000" floodOpacity="0.5" />
        </filter>

        {/* Mushroom Cap Drop Shadow */}
        <filter id={`capShadow-${uid}`} x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow
            dx="0"
            dy={isTriggered ? 2 : 5}
            stdDeviation={isTriggered ? 3 : 5.5}
            floodColor="#000000"
            floodOpacity={isTriggered ? 0.7 : 0.55}
          />
        </filter>

        {/* Tripped Alarm Pulse Filter */}
        <filter id={`alarmGlow-${uid}`} x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="3" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Circular text paths for curved legend markings */}
        <path id={`topArc-${uid}`} d="M 16,60 A 44 44 0 1 1 104,60" fill="none" />
        <path id={`bottomArc-${uid}`} d="M 104,60 A 44 44 0 0 1 16,60" fill="none" />
      </defs>

      <style>{`
        @keyframes estop-alarm-${uid} {
          0%, 100% { opacity: 0.95; transform: scale(1); }
          50% { opacity: 0.35; transform: scale(1.02); }
        }
        .estop-alarm-pulsing-${uid} {
          animation: estop-alarm-${uid} 0.9s ease-in-out infinite;
          transform-origin: 60px 60px;
        }
      `}</style>

      {/* Main Assembly Group */}
      <g id="emergency-stop-assembly">
        
        {/* Layer 1: Safety Yellow Circular Legend Plate with Panel Drop Shadow */}
        <g id="yellow-legend-plate" filter={`url(#baseShadow-${uid})`}>
          {/* Main Yellow Disk */}
          <circle cx="60" cy="60" r="56" fill={`url(#yellowPlateGrad-${uid})`} stroke="#854d0e" strokeWidth="1.2" />

          {/* Outer Chamfer Edge Highlight */}
          <circle cx="60" cy="59.4" r="55.2" fill="none" stroke="rgba(255, 255, 255, 0.45)" strokeWidth="0.8" />
          <circle cx="60" cy="60.6" r="55.2" fill="none" stroke="rgba(0, 0, 0, 0.35)" strokeWidth="0.8" />

          {/* Concentric Contrast Safety Rings */}
          <circle cx="60" cy="60" r="52.5" fill="none" stroke="rgba(0, 0, 0, 0.32)" strokeWidth="0.8" />
          <circle cx="60" cy="60" r="41.5" fill="none" stroke="rgba(0, 0, 0, 0.28)" strokeWidth="0.8" />

          {/* Curved Black Legend Plate Text (EMERGENCY / STOP) */}
          <text
            fill="#0f172a"
            fontSize="9"
            fontWeight="900"
            fontFamily="system-ui, -apple-system, sans-serif"
            letterSpacing="1.4px"
            textAnchor="middle"
            style={{ textShadow: '0 0.5px 0.5px rgba(255, 255, 255, 0.6)' }}
          >
            <textPath href={`#topArc-${uid}`} startOffset="50%" textAnchor="middle">
              {topText}
            </textPath>
          </text>

          <text
            fill="#0f172a"
            fontSize="9"
            fontWeight="900"
            fontFamily="system-ui, -apple-system, sans-serif"
            letterSpacing="1.4px"
            textAnchor="middle"
            style={{ textShadow: '0 0.5px 0.5px rgba(255, 255, 255, 0.6)' }}
          >
            <textPath href={`#bottomArc-${uid}`} startOffset="50%" textAnchor="middle">
              {bottomText}
            </textPath>
          </text>
        </g>

        {/* Layer 2: Heavy-Duty Cast Anthracite Collar / Bezel */}
        <g id="mounting-collar">
          <circle cx="60" cy="60" r="39.5" fill={`url(#bezelGrad-${uid})`} stroke="#090d16" strokeWidth="1.2" />
          {/* Bezel 3D Highlight Bevel */}
          <circle cx="60" cy="59.3" r="38.8" fill="none" stroke="rgba(255, 255, 255, 0.25)" strokeWidth="0.7" />
          {/* Inner Recess Hole */}
          <circle cx="60" cy="60" r="36" fill="#090d16" stroke="#000000" strokeWidth="1" />

          {/* Latched / Triggered Warning Collar Ring */}
          {isTriggered && (
            <circle
              cx="60"
              cy="60"
              r="37"
              fill="none"
              stroke="#ef4444"
              strokeWidth="2.2"
              className={`estop-alarm-pulsing-${uid}`}
              filter={`url(#alarmGlow-${uid})`}
            />
          )}

          {/* Chrome Shaft Ring (Visible in normal armed state, hidden when pressed) */}
          {!isTriggered && (
            <ellipse cx="60" cy="59" rx="27" ry="26" fill={`url(#chromeShaftGrad-${uid})`} opacity="0.9" />
          )}
        </g>

        {/* Layer 3: 3D Red Mushroom Head (Mantar Kafa) with Mechanical Transform */}
        <g
          id="mushroom-head"
          filter={`url(#capShadow-${uid})`}
          style={{
            transform: isTriggered ? 'translate(0px, 3.2px) scale(0.94)' : 'translate(0px, 0px) scale(1)',
            transformOrigin: '60px 60px',
            transition: 'transform 0.14s cubic-bezier(0.34, 1.45, 0.64, 1)',
          }}
        >
          {/* Outer Ergonomic Fluted Grip Ridge of Mushroom */}
          <circle
            cx="60"
            cy={isTriggered ? 60.5 : 58.5}
            r="33.5"
            fill={isTriggered ? `url(#mushroomPressedGrad-${uid})` : `url(#mushroomDomeGrad-${uid})`}
            stroke="#450a0a"
            strokeWidth="1.2"
          />

          {/* Mushroom Cap Crown Dome Highlight */}
          <ellipse
            cx="58"
            cy={isTriggered ? 53 : 50}
            rx="21"
            ry="11"
            fill="rgba(255, 255, 255, 0.22)"
            pointerEvents="none"
          />

          {/* Sharp Specular Apex Glint */}
          <ellipse
            cx="54"
            cy={isTriggered ? 49 : 46}
            rx="9"
            ry="4.5"
            fill="rgba(255, 255, 255, 0.42)"
            pointerEvents="none"
          />

          {/* Underside Cap Rim Shadow Curve */}
          <path
            d="M 28 65 C 36 84 84 84 92 65"
            fill="none"
            stroke="rgba(0, 0, 0, 0.45)"
            strokeWidth="1.8"
            pointerEvents="none"
          />

          {/* 3 Clockwise Twist-to-Reset Direction Arrows (Standard ISO 13850) */}
          <g id="twist-arrows" transform={`translate(60, ${isTriggered ? 60.5 : 58.5})`} opacity={isTriggered ? 0.75 : 0.92}>
            {[0, 120, 240].map((rot) => (
              <g key={`arrow-${rot}`} transform={`rotate(${rot})`}>
                {/* Curved Arrow Body Arc */}
                <path
                  d="M 13.5,-9.5 A 16.5 16.5 0 0 1 15.6,5.5"
                  fill="none"
                  stroke="#ffffff"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  style={{ filter: 'drop-shadow(0 0.8px 1px rgba(0,0,0,0.5))' }}
                />
                {/* Clockwise Arrowhead */}
                <polygon
                  points="11.8,4.0 16.2,8.8 19.0,3.2"
                  fill="#ffffff"
                  style={{ filter: 'drop-shadow(0 0.8px 1px rgba(0,0,0,0.5))' }}
                />
              </g>
            ))}

            {/* Center Concave Tactile Indent */}
            <circle cx="0" cy="0" r="7.2" fill={`url(#centerIndentGrad-${uid})`} stroke="rgba(0, 0, 0, 0.3)" strokeWidth="0.8" />
            <circle cx="0" cy="0" r="3.2" fill="#7f1d1d" opacity="0.6" />
          </g>
        </g>
      </g>
    </svg>
  );
};
